// Same-origin fetch wrapper for the three apps. Cookies are httpOnly (never readable here); every app talks to /api
// through its own origin (Vite proxy in development, the app's reverse proxy in production).

export interface ApiErrorBody { code: string; message: string; detail?: unknown; requestId?: string }

export class ApiError extends Error {
  constructor(public status: number, public body: ApiErrorBody) { super(body.message); this.name = 'ApiError'; }
  get code() { return this.body.code; }
}

/** Fired when the server says the session is gone, so the app can return to sign-in. */
export const SESSION_EXPIRED_EVENT = 'ps:session-expired';

export interface RequestOptions { idempotencyKey?: string; signal?: AbortSignal }

export async function api<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  const res = await fetch(`/api${path}`, {
    method, headers, credentials: 'same-origin', signal: opts.signal,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new ApiError(res.status, json?.error ?? { code: 'NETWORK', message: 'Something went wrong. Please try again.' });
    // STEP_UP_REQUIRED is a 401 that keeps the session: the app asks for a code and retries (it must not sign out).
    if (res.status === 401 && !path.startsWith('/auth/') && err.code !== 'STEP_UP_REQUIRED') window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    throw err;
  }
  return json as T;
}

/** Uploads a file as the raw request body (documents: PDF/JPEG/PNG). */
export async function apiUpload<T>(path: string, file: Blob, query: Record<string, string | undefined>): Promise<T> {
  const qs = new URLSearchParams(Object.entries(query).filter((e): e is [string, string] => !!e[1])).toString();
  const res = await fetch(`/api${path}?${qs}`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': file.type || 'application/octet-stream', Accept: 'application/json' }, body: file });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, json?.error ?? { code: 'NETWORK', message: 'Something went wrong. Please try again.' });
  return json as T;
}

/** Downloads a file the server serves inline (staff document viewer). */
export async function apiBlob(path: string): Promise<Blob> {
  const res = await fetch(`/api${path}`, { credentials: 'same-origin' });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new ApiError(res.status, json?.error ?? { code: 'NETWORK', message: 'Something went wrong. Please try again.' });
  }
  return res.blob();
}

/** A fresh key per user action (e.g. one "Confirm and send" click), reused only for retries of that same action. */
export const newIdempotencyKey = () => crypto.randomUUID();
